import { useState } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { useEffect } from 'react';
import { updateSEO } from '../../../utils/seo';
import { FORM_ENDPOINT } from '../../../config/form';
import { MEDIA_INQUIRY_TYPE, ATTACH_ACCEPT, checkAttachments, filesToPayload } from '../../../config/attachments';

// ===== EN Contact — full redesign (new design charter). Form submission logic is UNCHANGED. =====

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    inquiryType: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitErrorMessage, setSubmitErrorMessage] = useState('');
  const [formShownAt, setFormShownAt] = useState(() => Date.now());
  // Media & press inquiries may attach materials (3 files, 10MB in total — same limits as the receiving GAS). Added 2026-10-08.
  const [files, setFiles] = useState<File[]>([]);
  const isMedia = formData.inquiryType === MEDIA_INQUIRY_TYPE;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    // Leaving the media type clears the files so hidden old attachments are never sent.
    if (name === 'inquiryType' && value !== MEDIA_INQUIRY_TYPE) setFiles([]);
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.name || !formData.email || !formData.inquiryType || !formData.subject || !formData.message) {
      alert('Please fill in all required fields.');
      return;
    }

    if (formData.message.length > 500) {
      alert('Please limit your message to 500 characters.');
      return;
    }

    const attachError = isMedia ? checkAttachments(files, 'en') : '';
    if (attachError) {
      setSubmitStatus('error');
      setSubmitErrorMessage(attachError);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setSubmitErrorMessage('');

    if (!FORM_ENDPOINT) {
      setSubmitStatus('error');
      setSubmitErrorMessage('The contact form is currently being prepared. Please contact us by phone.');
      setIsSubmitting(false);
      return;
    }

    try {
      const attachments = isMedia && files.length ? await filesToPayload(files) : [];
      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({
          form: 'contact',
          lang: 'en',
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          category: formData.inquiryType,
          company: formData.company,
          subject: formData.subject,
          message: formData.message,
          _hp: '',
          page: window.location.href,
          userAgent: navigator.userAgent,
          formShownAt,
          attachments
        })
      });
      const result = await response.json() as { status?: string };

      if (response.ok && result.status === 'ok') {
        setSubmitStatus('success');
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          inquiryType: '',
          subject: '',
          message: ''
        });
        setFiles([]);
        setFormShownAt(Date.now());
      } else {
        setSubmitStatus('error');
        setSubmitErrorMessage('Submission failed. Please contact us by phone at 03-6824-4086.');
      }
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus('error');
      setSubmitErrorMessage('Submission failed. Please contact us by phone at 03-6824-4086.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inquiryTypes = [
    { value: '', label: 'Please select inquiry type' },
    { value: 'membership', label: 'Membership Information' },
    { value: 'qualification', label: 'Certification Programs' },
    { value: 'seminar', label: 'Seminars & Events' },
    { value: 'corporate', label: 'Corporate Services' },
    { value: 'media', label: 'Media & Press Inquiries' },
    { value: 'partnership', label: 'Partnership Opportunities' },
    { value: 'other', label: 'Other' }
  ];

  const contactMethods = [
    {
      icon: 'ri-phone-line',
      title: 'Phone Inquiries',
      content: '03-6824-4086',
      subContent: 'Available: Year-round',
      bgImage: '/images/generated/jba-fs-en-004-contact.webp'
    },
    {
      icon: 'ri-mail-line',
      title: 'Email Inquiries',
      content: 'info@butler-association.jp',
      subContent: '24/7 (Response during business hours)',
      bgImage: '/images/generated/jba-fs-en-005-contact.webp'
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Office Visits',
      content: 'Please check access information',
      subContent: 'By appointment only',
      bgImage: '/images/salon0926/20260912_115224.jpg',
      imageAlt: 'The brass lock on a wooden door in the butler salon'
    }
  ];

  const faqs = [
    { q: 'Will I receive job placement assistance after obtaining certification?', a: 'Our association specializes in certification and education, and does not provide job placement or staffing services. However, we may share industry information and job opportunities with members.\n\nJob hunting activities are conducted independently by each individual.' },
    { q: 'Can I obtain butler certification without experience?', a: 'Yes, you can. The Associate Butler (entry-level certification) is designed for those without experience. We recommend starting with introductory seminars such as the Butler Basics Course.\n\nPlease check our "Certifications" page for the certification process.' },
    { q: 'Can I take certification exams without membership?', a: 'Membership is not required to take certification exams. However, members receive benefits such as discounted exam fees and seminar privileges. We recommend membership for those planning continuous learning.' },
    { q: 'Can I access seminar recordings or download materials?', a: 'Members can access archived videos and download materials for selected seminars. Non-members should check individual seminar pages or contact us for details.' },
    { q: 'We\'d like to support multiple employees in obtaining certifications.', a: 'We offer corporate membership programs with benefits including multi-employee certification support, corporate training programs, and priority seminar access. Please check our "Membership" page or contact us by phone for details.' },
    { q: 'Can I visit your office?', a: 'Office visits are by appointment only. Please contact us by phone (03-6824-4086) or email (info@butler-association.jp) to schedule. We\'re open year-round, but may not be able to accommodate all requested times, so please contact us in advance.' },
    { q: 'Can I obtain certification if I live overseas?', a: 'Certification exams are generally conducted in Japan, but online testing is available for some certifications. Some seminars are also held online. Please contact us for specific details.' },
    { q: 'How long does it take to receive a response to inquiries?', a: 'We respond to email inquiries within 2 business days. For urgent matters, please call us at 03-6824-4086. Please note that some inquiries may require additional time for a complete response.' },
  ];

  const accessFacts = [
    { icon: 'ri-map-pin-line', label: 'Address', value: '2-7-4 Aomi, Koto-ku, Tokyo 135-0064' },
    { icon: 'ri-phone-line', label: 'Phone', value: '03-6824-4086' },
    { icon: 'ri-time-line', label: 'Business hours', value: 'Year-round (office visits by appointment only)\nPlease call or email to schedule' },
    { icon: 'ri-train-line', label: 'Nearest stations', value: '5 min walk from Tokyo International Cruise Terminal Station\n5 min walk from Telecom Center Station\n15 min walk from Tokyo Teleport Station' },
  ];

  const breadcrumbs = [
    { label: 'Home', href: '/en' },
    { label: 'Contact' }
  ];

  const INPUT = "w-full px-4 py-3 border border-[#d8d2c4] bg-white focus:outline-none focus:border-[#a38e65] focus:ring-1 focus:ring-[#a38e65] transition-colors duration-300 text-sm font-['Noto_Serif_JP',serif] text-[#1a2942]";
  const LABEL = "block text-sm font-medium text-[#1a2942] mb-2 font-['Noto_Serif_JP',serif]";

  useEffect(() => {
    updateSEO({
      title: 'Contact Us | Japan Butler Association',
      description: 'Contact us for inquiries about certification, seminars, and membership. Located in Koto-ku, Tokyo, five minutes from Tokyo International Cruise Terminal Station. Phone, email, and visit consultations available. TEL 03-6824-4086',
      keywords: 'contact butler association,butler inquiry,butler consultation,Tokyo butler,butler information,butler questions',
      path: '/en/contact'
    });
  }, []);

  return (
    <Layout>
      {/* Hero */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[480px] lg:min-h-[540px] flex items-center">
        <img src="/images/salon0926/1786974370556.jpg" alt="The fireplace and sofa seating area in the butler salon" className="absolute inset-0 w-full h-full object-cover object-[center_65%]" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.7)_50%,rgba(16,20,26,0.4)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[640px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Contact</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(26px,3.2vw,42px)] leading-[1.4] tracking-[0.02em] mb-6">
              Contact Us
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.9] mb-8">
              We welcome your questions and requests about certification, seminars, membership, corporate services, and media. Reach us by phone, email, or the form below — whichever suits you best. We reply within two business days.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="#contact-form" className="relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#f8f6f0] hover:text-[#1a2942] cursor-pointer whitespace-nowrap">Contact Form</a>
              <a href="tel:03-6824-4086" className="inline-flex items-center justify-center gap-2 border border-[#a38e65] text-[#f8f6f0] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] whitespace-nowrap"><i className="ri-phone-line"></i>Call Us</a>
            </div>
          </div>
        </div>
      </section>
      {/* パンくず: ビルボード直後に配置（Yuichi 2026-07-17 指摘②）。共通Breadcrumbのヘッダー逃げpaddingを子セレクタで通常paddingへ上書き */}
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      {/* Contact Methods */}
      <section className="py-14 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-4xl mb-10 text-left">
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[30px] leading-[1.5] tracking-[0.01em] mb-5">
              Three ways to reach us, <span className="text-[#a38e65]">to suit you</span>
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#333] leading-[1.9]">
              Choose from phone, email, or an office visit. For urgent matters, a phone call is the surest. Office visits are by appointment only, so please contact us in advance.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {contactMethods.map((method) => (
              <div key={method.title} className="group relative overflow-hidden border border-[#e2ddd2]">
                <div className="relative h-72">
                  <img src={method.bgImage} alt={'imageAlt' in method ? method.imageAlt : method.title} className="absolute inset-0 w-full h-full object-cover object-top" style={method.bgImage === '/images/salon0926/20260912_115224.jpg' ? { objectPosition: '50% 65%' } : undefined} />
                  <div className="absolute inset-0 bg-[linear-gradient(to_top,rgba(16,20,26,0.94)_0%,rgba(16,20,26,0.55)_55%,rgba(16,20,26,0.2)_100%)]"></div>
                  <div className="absolute inset-0 p-7 flex flex-col justify-end text-left">
                    <i className={`${method.icon} text-[#d7b779] text-3xl mb-4`}></i>
                    <h3 className="jba-display font-medium text-[#f8f6f0] text-lg mb-2">{method.title}</h3>
                    <p className="font-['Playfair_Display',serif] text-[#f8f6f0] text-lg mb-1">{method.content}</p>
                    <p className="font-['Noto_Serif_JP',serif] text-[13px] text-[#c9c2b4]">{method.subContent}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form (submission logic unchanged; visuals only) */}
      <section id="contact-form" className="py-14 sm:py-16 bg-[#f8f6f0]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="mb-10 text-center">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-[11px] text-[#8f7a56] mb-3">Inquiry Form</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[30px] leading-[1.5] tracking-[0.01em] mb-4">
              Send us a message
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-sm sm:text-base text-[#4a4638] leading-[1.85]">
              Please fill out the form below and we will respond within two business days.
            </p>
          </div>

          <div className="bg-white border border-[#e2ddd2] p-7 sm:p-10">
            <form onSubmit={handleSubmit} id="contact-form-element">
              <input type="hidden" name="formShownAt" value={formShownAt} />
              <div className="grid md:grid-cols-2 gap-5 mb-5">
                <div>
                  <label htmlFor="name" className={LABEL}>Name <span className="text-[#a38e65]">*</span></label>
                  <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} required className={INPUT} placeholder="John Smith" />
                </div>
                <div>
                  <label htmlFor="email" className={LABEL}>Email Address <span className="text-[#a38e65]">*</span></label>
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} required className={INPUT} placeholder="example@email.com" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-5 mb-5">
                <div>
                  <label htmlFor="phone" className={LABEL}>Phone Number</label>
                  <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleInputChange} className={INPUT} placeholder="03-1234-5678" />
                </div>
                <div>
                  <label htmlFor="company" className={LABEL}>Company / Organization</label>
                  <input type="text" id="company" name="company" value={formData.company} onChange={handleInputChange} className={INPUT} placeholder="Company Name" />
                </div>
              </div>

              <div className="mb-5">
                <label htmlFor="inquiryType" className={LABEL}>Inquiry Type <span className="text-[#a38e65]">*</span></label>
                <div className="relative">
                  <select id="inquiryType" name="inquiryType" value={formData.inquiryType} onChange={handleInputChange} required className={`${INPUT} pr-8 appearance-none cursor-pointer`}>
                    {inquiryTypes.map((type) => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-[#a38e65] pointer-events-none"></i>
                </div>
              </div>

              <div className="mb-5">
                <label htmlFor="subject" className={LABEL}>Subject <span className="text-[#a38e65]">*</span></label>
                <input type="text" id="subject" name="subject" value={formData.subject} onChange={handleInputChange} required className={INPUT} placeholder="Enter your inquiry subject" />
              </div>

              <div className="mb-6">
                <label htmlFor="message" className={LABEL}>Message <span className="text-[#a38e65]">*</span></label>
                <textarea id="message" name="message" value={formData.message} onChange={handleInputChange} required maxLength={500} rows={6} className={`${INPUT} resize-none`} placeholder="Please provide details about your inquiry (500 characters max)" />
                <div className="text-right text-xs text-[#8f7a56] mt-1 font-['Noto_Serif_JP',serif]">{formData.message.length}/500 characters</div>
              </div>

              {isMedia && (
                <div className="mb-6">
                  <label htmlFor="attachments" className={LABEL}>Attach Materials <span className="text-xs text-[#6b5a3d]">(optional)</span></label>
                  <input
                    type="file"
                    id="attachments"
                    name="attachments"
                    multiple
                    accept={ATTACH_ACCEPT}
                    onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
                    className="w-full text-sm text-[#1a2942] file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-[#f3efe6] file:text-[#1a2942] hover:file:bg-[#e9e2d2] font-['Noto_Serif_JP',serif]"
                  />
                  <p className="text-xs text-[#6b5a3d] mt-1 font-['Noto_Serif_JP',serif]">Interview requests, proposals or media kits — up to 3 files, 10MB in total (PDF, Word, PowerPoint, Excel, JPEG, PNG, HEIC).</p>
                  {files.length > 0 && (
                    <p className="text-xs text-[#6b5a3d] mt-1 font-['Noto_Serif_JP',serif] [overflow-wrap:anywhere]">
                      Attached files: {files.map(file => file.name).join(' / ')}
                    </p>
                  )}
                </div>
              )}

              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-[#f0f4ee] border border-[#9bb08a]">
                  <div className="flex items-center gap-2 font-['Noto_Serif_JP',serif] text-sm text-[#3f5a34]">
                    <i className="ri-check-line text-lg"></i>
                    <p>Thank you for your inquiry. We will respond within 2 business days.</p>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-[#f7ecec] border border-[#c98f8f]">
                  <div className="flex items-center gap-2 font-['Noto_Serif_JP',serif] text-sm text-[#8f3f3f]">
                    <i className="ri-error-warning-line text-lg"></i>
                    <p>{submitErrorMessage}</p>
                  </div>
                </div>
              )}

              <div className="text-center">
                <button
                  type="submit"
                  disabled={formData.message.length > 500 || isSubmitting}
                  className="relative inline-flex items-center justify-center gap-2 bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[52px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none"
                >
                  {isSubmitting ? (<><i className="ri-loader-4-line animate-spin"></i>Sending...</>) : (<><i className="ri-send-plane-line"></i>Submit</>)}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-4xl mb-8 text-left">
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[30px] leading-[1.5] tracking-[0.01em] mb-5">
              Before you write, <span className="text-[#a38e65]">frequently asked questions</span>
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#333] leading-[1.9]">
              We have gathered the questions we receive most often about certification, exams, membership, and visits. Checking these first may help you find an answer more quickly.
            </p>
          </div>
          <div className="max-w-4xl border-t border-[#e2ddd2]">
            {faqs.map((f) => (
              <div key={f.q} className="border-b border-[#e2ddd2] py-6">
                <h3 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl leading-[1.5] mb-3 flex items-start gap-3">
                  <span className="font-['Playfair_Display',serif] text-[#a38e65] text-xl leading-none pt-0.5">Q.</span><span>{f.q}</span>
                </h3>
                <div className="space-y-4">{f.a.split(/\n{2,}/).map((s, i) => (
                  <p key={i} className="font-['Noto_Serif_JP',serif] text-sm sm:text-base text-[#4a4638] leading-[1.9] pl-8">{s.trim()}</p>
                ))}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Access */}
      <section className="py-14 sm:py-16 bg-[#f8f6f0]">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-4xl mb-10 text-left">
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[30px] leading-[1.5] tracking-[0.01em] mb-5">
              Access to <span className="text-[#a38e65]">our office</span>
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#333] leading-[1.9]">
              Our office is in Aomi, Odaiba. If you wish to visit, please make an appointment in advance.
            </p>
          </div>
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            <div>
              <div className="bg-white border border-[#e2ddd2] p-7 sm:p-9">
                <div className="mb-6">
                  <div className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1">Official name</div>
                  <div className="jba-display font-medium text-[#1a2942] text-lg">Japan Butler Association</div>
                  <div className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] mt-1">Corporate Number: 2010005039584</div>
                </div>
                <dl className="border-t border-[#e2ddd2]">
                  {accessFacts.map((f) => (
                    <div key={f.label} className="flex gap-4 border-b border-[#e2ddd2] py-4">
                      <span className="w-9 h-9 shrink-0 border border-[#a38e65]/40 flex items-center justify-center text-[#a38e65]"><i className={f.icon}></i></span>
                      <div>
                        <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1">{f.label}</dt>
                        <dd className="font-['Noto_Serif_JP',serif] text-sm text-[#1a2942] leading-[1.8] whitespace-pre-line">{f.value}</dd>
                      </div>
                    </div>
                  ))}
                </dl>
                <div className="mt-6 p-4 bg-[#f8f6f0] border border-[#a38e65]/30">
                  <p className="font-['Noto_Serif_JP',serif] text-[13px] text-[#4a4638] leading-[1.8]">
                    <span className="text-[#1a2942] font-medium">Important notice</span><br />
                    An advance appointment is required for all visits. Please contact us by phone or email.
                  </p>
                </div>
              </div>
            </div>
            <div className="border border-[#a38e65]/30 overflow-hidden">
              <iframe
                src="https://www.google.com/maps?q=35.6297,139.7745&output=embed"
                width="100%"
                height="460"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Japan Butler Association Office Map"
              />
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
